import { ApplyDiscountRequest } from 'models/Discount'
import { OrderType } from 'models/Order'
import { Proteins } from 'models/Pet'
import { ProductVariant, ProductTypes } from 'models/Product'
import { QueryClient, useQuery, UseQueryOptions } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import { ApiClient } from 'services/ApiClient'
import { Api } from 'types/Api'
import { objectToQueryString } from 'utils/objectToQueryString'

export namespace FetchProductVariants {
  export type Response = Api.PaginatedResponse<ProductVariant, 'productVariants'>
  export type Error = Api.Error
  export type Options = UseQueryOptions<Response, Error>
}

export type ProductVariantsRequest = {
  page: number
  limit: number
  proteins?: Proteins[]
  types?: ProductTypes[]
  textSearch?: string
  discount?: ApplyDiscountRequest
}

const createKey = (productVariantsRequest: ProductVariantsRequest) => [
  'product-variants',
  productVariantsRequest,
]

const queryFetcher =
  ({
    discount = { orderType: OrderType.PRODUCTS_SUBSCRIPTION },
    ...productVariantsRequest
  }: ProductVariantsRequest) =>
  () => {
    const queryString = objectToQueryString({ ...productVariantsRequest })
    return ApiClient.post<FetchProductVariants.Response>(
      `/v1/product-variants/preview${queryString}`,
      {
        data: { discount },
      }
    )
  }

export const useFetchProductVariants = (
  productVariantsRequest: ProductVariantsRequest,
  options?: FetchProductVariants.Options
) => {
  return useQuery<FetchProductVariants.Response, FetchProductVariants.Error>(
    createKey(productVariantsRequest),
    queryFetcher(productVariantsRequest),
    options
  )
}

export const prefetchFetchProductVariants = async (
  productVariantsRequest: ProductVariantsRequest,
  options?: FetchProductVariants.Options
) => {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery<FetchProductVariants.Response, FetchProductVariants.Error>(
    createKey(productVariantsRequest),
    queryFetcher(productVariantsRequest),
    options
  )

  return {
    dehydratedState: dehydrate(queryClient),
  }
}
